/**
 * V113-7 — pure display helpers for the Monitoring screens (Monitoring page, system Monitoring tab, configuration
 * item page, public verify page, program Operate card). Words come from what the API already returns; nothing
 * here invents a fact. No React.
 */
import type { CarriedCounts, ConfigChangeCause, NextMonitoringAction, PackValidityStatus, SystemPackState } from '@aigovops/core';
import { scopeOutFailing, scopeOutPassing, type Capability, type CapabilityContext } from './monitoring-capabilities';
import type { MonitoringSummaryRow } from './monitoring-summary';
import { fmtDate, plural } from './format';

export type ChipTone = 'red' | 'amber' | 'ok' | 'outline' | 'neutral' | 'blue' | 'muted';

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const CLASS_LABELS: Record<string, string> = {
  chatbot: 'Chatbot', agent: 'Agent', rag: 'RAG', fine_tuned_model: 'Fine-tuned model', embedded_ml: 'Embedded ML',
  agentic_systems: 'Agentic system', ai_agents: 'AI agent', llm_services: 'LLM service', ml_models: 'ML model', data_pipelines: 'Data pipeline',
};
/** The classes offered when a member sets or changes a class (the prototype's five). */
export const CLASS_CHOICES = ['chatbot', 'agent', 'rag', 'fine_tuned_model', 'embedded_ml'] as const;

const humanize = (s: string) => s.replace(/[_-]+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());

export const classLabel = (c: string | null | undefined): string => (c ? CLASS_LABELS[c] ?? humanize(c) : 'No class');

/** In-sentence class phrases ("an embedded ML system") with the right article — never a lowercased enum. */
const CLASS_PHRASES: Record<string, string> = {
  chatbot: 'a chatbot', agent: 'an agent', rag: 'a RAG', fine_tuned_model: 'a fine-tuned model', embedded_ml: 'an embedded ML',
};
export function classPhrase(c: string | null | undefined): string {
  if (!c) return 'this';
  const p = CLASS_PHRASES[c];
  if (p) return p;
  const label = classLabel(c);
  return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label.toLowerCase()}`;
}

const KIND_LABELS: Record<string, string> = {
  app: 'App', bucket: 'Storage bucket', guardrail: 'Guardrail', vector_index: 'Vector index', ai_gateway: 'AI gateway',
  mcp_server: 'MCP server', queue: 'Queue', saas_app: 'SaaS app', model: 'Model endpoint', pipeline: 'Pipeline',
};
export const kindLabel = (k: string | null | undefined): string => (k ? KIND_LABELS[k] ?? humanize(k) : 'Item');

const PROVIDER_LABELS: Record<string, string> = {
  aws: 'AWS', gcp: 'Google Cloud', azure: 'Azure', cloudflare: 'Cloudflare', github: 'GitHub', pinecone: 'Pinecone', okta: 'Okta',
  datadog: 'Datadog', entra: 'Entra', openai: 'OpenAI', anthropic: 'Anthropic',
};
export const providerLabel = (p: string | null | undefined): string => (p ? PROVIDER_LABELS[p] ?? humanize(p) : '');

const ENV_LABELS: Record<string, string> = { prod: 'prod', staging: 'staging', dev: 'dev', transient: 'transient' };
export const envLabel = (e: string | null | undefined): string => (e ? ENV_LABELS[e] ?? e : 'no environment');

const DETECTOR_KIND_LABELS: Record<string, string> = {
  connector_check: 'Connector check', ci_parser: 'Pipeline result', aggregate_rule: 'Usage rule', attestation: 'Attestation', code_library: 'Code library',
};
export const detectorKindLabel = (k: string | null | undefined): string => (k ? DETECTOR_KIND_LABELS[k] ?? humanize(k) : 'Method');

const DETECTOR_REF_LABELS: Record<string, string> = {
  cf_ai_gateway_logs: 'Cloudflare AI Gateway logs', nemo_guardrails: 'NeMo Guardrails', datadog_monitor: 'Datadog monitor',
  langsmith: 'LangSmith tracing', bedrock_invocation_metrics: 'Bedrock usage metrics', promptfoo: 'promptfoo eval',
  ai_guardrails_configured: 'Guardrail config check', cost_spike: 'Cost spike rule', volume_change: 'Volume change rule',
  error_rate_elevation: 'Error-rate rule', eval_report: 'Eval report', check: 'Config check',
};
/** A method or candidate in people's words ("LangSmith tracing"); attestation refs read "Attestation". */
export function detectorLabel(kind: string | null | undefined, ref: string | null | undefined): string {
  if (kind === 'attestation') return 'Attestation';
  if (ref && DETECTOR_REF_LABELS[ref]) return DETECTOR_REF_LABELS[ref]!;
  return ref ? humanize(ref) : detectorKindLabel(kind);
}

export const FIT_LABELS: Record<string, string> = { native: 'Native', partial: 'Partial', substitute: 'Substitute' };
export const fitLabel = (f: string | null | undefined) => (f ? FIT_LABELS[f] ?? humanize(f) : '');

const METHOD_STATE_LABELS: Record<string, string> = { identified: 'Identified', verified: 'Verified', mapped: 'Mapped', retired: 'Retired' };
export const methodStateLabel = (s: string) => METHOD_STATE_LABELS[s] ?? humanize(s);

const VIA_LABELS: Record<string, string> = { bom: 'Via the BOM', boundary: 'Via the boundary', infra: 'Via infra inventory', manual: 'Manually attributed' };
export const viaLabel = (v: string | null | undefined) => (v ? VIA_LABELS[v] ?? humanize(v) : '');

const RECIPIENT_LABELS: Record<string, string> = { insurer: 'Insurer', customer: 'Customer' };
export const recipientTypeLabel = (t: string) => RECIPIENT_LABELS[t] ?? humanize(t);

/** Pack-validity reason codes, short ("Control stopped passing"). The server's own `label` is preferred where present. */
const REASON_SHORT: Record<string, string> = {
  control_degradation: 'Control stopped passing', monitoring_testing_overdue: 'Check overdue', monitoring_evidence_stale: 'Evidence past its window',
  new_system_not_in_pack: 'New system not in pack', new_system_without_authority: 'New system without authority', authority_drift: 'Agent authority drift',
  new_unmonitored_applicable_risk: 'New unmonitored risk', new_applicable_risk_not_in_pack: 'New risk not in pack', incident_above_threshold: 'Incident above threshold',
  collector_unhealthy: 'Collector not reporting', ci_gone: 'Configuration item gone', ci_out_of_scope_failing: 'Failing item taken out of scope',
};
export const reasonShort = (code: string) => REASON_SHORT[code] ?? humanize(code);

// ---------------------------------------------------------------------------
// Pack state
// ---------------------------------------------------------------------------

export interface ChipMeta { label: string; tone: ChipTone; glyph: 'x' | 'clock' | 'warning' | 'check' | 'circle' }

const PACK_META: Record<PackValidityStatus | SystemPackState, ChipMeta> = {
  current: { label: 'Current', tone: 'outline', glyph: 'check' },
  current_with_exceptions: { label: 'Current with exceptions', tone: 'amber', glyph: 'warning' },
  stale: { label: 'Stale', tone: 'amber', glyph: 'clock' },
  invalidated: { label: 'Invalidated', tone: 'red', glyph: 'x' },
  not_in_pack: { label: 'Not in pack', tone: 'amber', glyph: 'warning' },
};
export const NO_PACK_META: ChipMeta = { label: 'No pack yet', tone: 'outline', glyph: 'circle' };
export const packMeta = (s: PackValidityStatus | SystemPackState | null | undefined): ChipMeta => (s ? PACK_META[s] ?? NO_PACK_META : NO_PACK_META);

/** "1 failing · 2 overdue" (only the non-zero groups); empty when nothing is carried. */
export function carriedText(c: CarriedCounts | null | undefined): string {
  if (!c) return '';
  return [c.failing ? `${c.failing} failing` : '', c.overdue ? `${c.overdue} overdue` : ''].filter(Boolean).join(' · ');
}

/** "Carried: 1 failing control · 2 overdue checks" for a system row. */
export function carriedLine(c: CarriedCounts | null | undefined): string {
  if (!c || c.failing + c.overdue === 0) return '';
  const parts = [c.failing ? plural(c.failing, 'failing control') : '', c.overdue ? plural(c.overdue, 'overdue check') : ''].filter(Boolean);
  return `Carried: ${parts.join(' · ')}`;
}

export const attestedLine = (pass: number | null | undefined, of: number | null | undefined): string =>
  pass === null || pass === undefined || of === null || of === undefined ? '' : `attested at ${pass} of ${of}`;

export const issuesText = (n: number): string => (n === 0 ? 'No open issues' : plural(n, 'open issue'));

// ---------------------------------------------------------------------------
// D-113-31: New (signed) and In progress (internal); a due date only from sla_due
// ---------------------------------------------------------------------------

/** "New · 0 of 6" while the system's methods have not reported (the signed marker). */
export function newMarkerText(row: { new_since_pack: boolean; passing: number; of: number }): string | null {
  return row.new_since_pack ? `New · ${row.passing} of ${row.of}` : null;
}

const ACT_LINK_LABEL: Record<string, string> = { fulfill_pr_reported: 'Open PR', issue_filed: 'Open issue' };
/** "In progress · Lena Park · since Oct 22" (+ its link label when the act carried one); null when nobody acted. */
export function inProgressText(ip: { by_name: string | null; since: string } | null | undefined): string | null {
  if (!ip) return null;
  return ['In progress', ip.by_name, `since ${fmtDate(ip.since)}`].filter(Boolean).join(' · ');
}
export function inProgressLinkLabel(ip: { act: string; link: string | null } | null | undefined): string | null {
  if (!ip?.link) return null;
  return ACT_LINK_LABEL[ip.act] ?? 'Open link';
}

export const dueText = (slaDue: string | null | undefined): string | null => (slaDue ? `Due ${fmtDate(slaDue)}` : null);

/**
 * A " · "-separated metadata line built from its parts, dropping empty ones FIRST, so a missing
 * value (an unknown member name, no reason, no date) never leaves a "· ·" gap.
 */
export function metaLine(parts: ReadonlyArray<string | null | undefined | false>): string {
  return parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0).join(' · ');
}

// ---------------------------------------------------------------------------
// Next action → where it goes
// ---------------------------------------------------------------------------

export const systemTabHref = (prefix: string, systemId: string, anchor?: string) =>
  `${prefix}/systems/${encodeURIComponent(systemId)}?tab=monitoring${anchor ? `#${anchor}` : ''}`;
export const controlAnchor = (controlId: string) => `mon-ctl-${controlId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
export const CLASS_ANCHOR = 'mon-class';
export const ciHref = (prefix: string, componentId: string, systemId?: string | null) =>
  `${prefix}/monitoring/configuration-items/${encodeURIComponent(componentId)}${systemId ? `?system=${encodeURIComponent(systemId)}` : ''}`;
export const findingHref = (prefix: string, findingId: string) => `${prefix}/findings?finding=${encodeURIComponent(findingId)}`;

/** V113-7-2: the Findings list filtered to monitoring findings, optionally one system's (and one control's). */
export function monitoringFindingsHref(prefix: string, f: { system?: string | null; control?: string | null } = {}): string {
  const q = new URLSearchParams();
  if (f.system) q.set('system', f.system);
  if (f.control) q.set('control', f.control);
  else q.set('source', 'monitoring');
  return `${prefix}/findings?${q.toString()}`;
}

/** The system tab's What-changed section (the "See change" target of a config-change cause). */
export const CHANGES_ANCHOR = 'mon-changes';

/**
 * A config-change cause in one line: "Prompt-attack filter On → Off · Oct 21, 2026". The actor shows only when a
 * source recorded one (collectors don't); nothing here is guessed.
 */
export function causeText(c: ConfigChangeCause): string {
  const what = c.field_label ?? 'Configuration';
  const change = c.from && c.to ? `${what} ${c.from} → ${c.to}` : `${what} changed`;
  return [change, c.actor_name, c.at_label].filter(Boolean).join(' · ');
}

/**
 * Where a row's next action goes (`prefix` = /w/:slug/app). A fix / re-run / re-attest with a named configuration
 * item (B-2 `target.component_id`) goes straight to that item's page, where the fix leads.
 */
export function nextActionHref(prefix: string, a: NextMonitoringAction): string {
  const t = a.target;
  if (a.kind === 'open_finding' && t.finding_id) return findingHref(prefix, t.finding_id);
  if ((a.kind === 'fix_failing' || a.kind === 'rerun_check' || a.kind === 'reattest') && t.component_id) return ciHref(prefix, t.component_id, t.system_id);
  if (t.type === 'control' && t.control_id) return systemTabHref(prefix, t.system_id, controlAnchor(t.control_id));
  if (t.section === 'class') return systemTabHref(prefix, t.system_id, CLASS_ANCHOR);
  return systemTabHref(prefix, t.system_id, 'mon-controls');
}

const KIND_RANK: Record<NextMonitoringAction['kind'], number> = {
  fix_failing: 0, rerun_check: 1, reattest: 1, open_finding: 2, add_methods: 3, set_class: 4, confirm_class: 5,
};
const t = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.POSITIVE_INFINITY);

/** The ONE workspace-level next action (the Next strip): the most urgent row's own next action. */
export function pickNext(rows: MonitoringSummaryRow[]): MonitoringSummaryRow | null {
  const withAction = rows.filter((r) => r.next_action);
  withAction.sort((a, b) => KIND_RANK[a.next_action!.kind] - KIND_RANK[b.next_action!.kind] || t(a.sla_due) - t(b.sla_due) || a.name.localeCompare(b.name));
  return withAction[0] ?? null;
}

/** Worst first: invalidated → stale → exceptions → not in pack → current; then open issues. */
const PACK_RANK: Record<string, number> = { invalidated: 0, stale: 1, current_with_exceptions: 2, not_in_pack: 3, current: 4 };
export function worstFirst(rows: MonitoringSummaryRow[]): MonitoringSummaryRow[] {
  return [...rows].sort((a, b) => (PACK_RANK[a.pack_state ?? ''] ?? 5) - (PACK_RANK[b.pack_state ?? ''] ?? 5) || b.open_issues - a.open_issues || a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// Role gates (client mirror; the server decides)
// ---------------------------------------------------------------------------

/** Out of scope (836): a PASSING item → owner / admin / editor, a FAILING one → owner / admin; a reason always. */
export const scopeOutCapability = (ctx: CapabilityContext, failing: boolean): Capability => (failing ? scopeOutFailing(ctx) : scopeOutPassing(ctx));

/** "Ask Lena Park" when the item has an owner who could act, else "Ask an owner or admin". */
export function askText(cap: Capability, opts: { ownerName?: string | null; adminOnly?: boolean } = {}): string | null {
  if (cap.allowed) return null;
  if (!opts.adminOnly && opts.ownerName) return `Ask ${opts.ownerName}`;
  return 'Ask an owner or admin';
}

// ---------------------------------------------------------------------------
// Clipboard (visible failure)
// ---------------------------------------------------------------------------

/** Copy text; resolves false when the clipboard is unavailable or refused (callers say so on screen). */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Absolute url for a path the API returned relative ("/verify/…"). */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//.test(path)) return path;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

/** "Oct 2" this year, "Oct 2, 2025" otherwise. */
export function shortDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDate(iso);
  return d.getFullYear() === new Date().getFullYear() ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : fmtDate(iso);
}
